import { Box } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { AlertCircle } from '@factorialco/f0-react/icons/app'
import { MeetingEmployeeSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface EmployeeSelectorProps {
  employees: EntitySelectEntity[]
  setEmployees: (employees: EntitySelectEntity[]) => void
  singleSelector?: boolean
  onlyMyReportees?: boolean
  error?: string
  disabled?: boolean
}

const EmployeeSelector = ({
  employees,
  setEmployees,
  singleSelector = true,
  onlyMyReportees = true,
  disabled = false,
  error,
}: EmployeeSelectorProps) => {
  const { employee: currentEmployee } = useCurrentSession()

  const { data, loading: employeesLoading } = useQuery(MeetingEmployeeSelectorDocument, {
    variables: {
      onlyMyReportees,
    },
  })

  return (
    <Box gap="s8">
      <EntitySelect
        label={i18n.t('meetings.employee_selector.employee_placeholder')}
        hideLabel
        disabled={disabled}
        loading={employeesLoading}
        entities={
          data?.employees?.employees
            ?.filter(({ id }) => id !== currentEmployee?.id)
            .map(({ id, avatar, fullName }) => ({
              avatar: buildUrl(avatar?.url),
              expanded: false,
              id,
              name: fullName,
            })) || []
        }
        notFoundSubtitle={i18n.t('meetings.employee_selector.not_found_subtitle')}
        notFoundTitle={i18n.t('meetings.employee_selector.not_found_title')}
        onGroupChange={() => {}}
        onItemExpandedChange={() => {}}
        onSelect={(selection: EntitySelectEntity | EntitySelectEntity[] | null) => {
          if (selection === null) {
            setEmployees([])
          } else if (Array.isArray(selection) && !singleSelector) {
            setEmployees(selection)
          } else if (!Array.isArray(selection) && singleSelector) {
            setEmployees(selection ? [selection] : [])
          }
        }}
        searchPlaceholder={i18n.t('meetings.employee_selector.employee_search_placeholder')}
        selectedEntities={employees}
        selectedLabel={i18n.t('meetings.employee_selector.selected')}
        singleSelector={singleSelector}
        placeholder={i18n.t('meetings.employee_selector.employee_placeholder')}
        selectedItemsCopy={i18n.t('meetings.employee_selector.employees_selected')}
        groups={[]}
        selectedGroup="all"
        clearLabel={i18n.t('meetings.employee_selector.clear')}
        selectAllLabel={i18n.t('meetings.employee_selector.select_all')}
      />
      {error && (
        <Box gap="s4" flexDirection="row" alignItems="center">
          <F0Icon icon={AlertCircle} color="critical" />
          <p className="text-sm font-medium text-f1-foreground-critical">{error}</p>
        </Box>
      )}
    </Box>
  )
}

export default EmployeeSelector
